import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";

const HERO_IMAGES = {
  japanese: "https://images.unsplash.com/photo-1528164344705-47542687000d?w=800",
  spanish:  "https://images.unsplash.com/photo-1542224505-c5f4219a781e?w=800",
  french:   "https://images.unsplash.com/photo-1773017825298-eebc95b42089?w=800",
  german:   "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800",
  italian:  "https://images.unsplash.com/photo-1523906630133-f6934a1ab2b9?w=800",
  portuguese:"https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=800",
  korean:   "https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=800",
  mandarin: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=800",
  english:  "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800",
};

export default function LanguagePicker() {
  const nav = useNavigate();
  const { user, setLanguage } = useAuth();
  const [languages, setLanguages] = useState([]);
  const [selected, setSelected] = useState(user?.language || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/languages").then((r) => setLanguages(r.data));
  }, []);

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await setLanguage(selected);
      nav("/dashboard");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grain px-6 sm:px-10 py-10 max-w-6xl mx-auto">
      <header className="mb-10">
        <p className="text-sm uppercase tracking-widest text-muted-foreground">Step 1 of 1</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tight">Which language will you plant today?</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Pick one to start. You can switch anytime — your streak stays yours.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="language-grid">
        {languages.map((lang) => {
          const isSel = selected === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => setSelected(lang.code)}
              data-testid={`language-card-${lang.code}`}
              className={`relative text-left rounded-2xl overflow-hidden border-2 bg-card calendar-cell ${
                isSel ? "border-terracotta ring-2 ring-terracotta/40" : "border-border"
              }`}
            >
              <div className="h-28 w-full overflow-hidden bg-muted">
                <img
                  src={HERO_IMAGES[lang.code]}
                  alt={lang.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl">{lang.name}</div>
                    <div className="text-sm text-muted-foreground">{lang.native}</div>
                  </div>
                  <div className="text-2xl">{lang.flag}</div>
                </div>
              </div>
              {isSel && (
                <div className="absolute top-3 right-3 bg-terracotta text-white rounded-full p-1.5">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex justify-end">
        <Button
          onClick={confirm}
          disabled={!selected || busy}
          data-testid="confirm-language-button"
          className="rounded-full h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white"
        >
          {busy ? "Setting up…" : "Start learning"}
          <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
